<template>
  <div class="relative w-full">
    <div class="relative w-full">
      <input
        ref="inputRef"
        :id="id"
        :value="displayValue"
        type="text"
        :class="[
          'combobox-input w-full appearance-none pl-2 pr-12 py-2 border border-solid rounded text-sm bg-surface text-fg outline-none focus:shadow-[0_0_0_2px_rgb(var(--color-accent-subtle))]',
          'disabled:bg-muted disabled:cursor-not-allowed',
          hasError
            ? 'border-danger focus:shadow-[0_0_0_2px_rgb(var(--color-danger-subtle))]'
            : 'border-border focus:border-accent',
        ]"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="onSearchInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.escape="closeDropdown"
        @keydown.enter.prevent="selectFirstResult"
      />
      <div class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        <font-awesome-icon
          v-if="modelValue && isClearable"
          :icon="['fal', 'times']"
          class="clear-icon cursor-pointer p-1 text-fg-muted hover:text-fg"
          @click.prevent="clearValue"
        />
        <font-awesome-icon
          :icon="['fal', 'chevron-down']"
          :class="[
            'chevron-icon cursor-pointer transition-transform text-fg-muted',
            showDropdown ? 'rotate-180' : '',
          ]"
          @mousedown.prevent
          @click="onChevronClick"
        />
      </div>
    </div>
    <div v-if="hasError" class="text-danger text-sm mt-1">
      {{ displayError }}
    </div>
    <Teleport :to="teleportTarget">
      <div
        v-if="showDropdown"
        class="ics-autocomplete-dropdown-portal overflow-y-auto bg-surface border border-border rounded shadow-lg"
        :style="dropdownPanelStyle"
      >
        <div v-if="displayLoading" class="px-3 py-3 text-center text-fg-muted italic">Laster...</div>
        <div v-else-if="renderOptions.length === 0" class="px-3 py-3 text-center text-fg-muted italic">Ingen treff</div>
        <div
          v-for="opt in renderOptions"
          v-else
          :key="opt.value"
          :class="[
            'px-3 py-2 cursor-pointer text-fg border-b border-border last:border-b-0 hover:bg-muted',
            opt.value === modelValue ? 'option-selected bg-muted font-semibold' : '',
          ]"
          @mousedown.prevent="selectOption(opt)"
        >
          <font-awesome-icon
            v-if="opt.value === modelValue"
            :icon="['fal', 'check']"
            class="mr-2 text-accent"
          />
          {{ opt.label }}
        </div>
        <div v-if="displayHasMore" class="px-3 py-2 text-xs text-fg-muted bg-muted border-t border-border">
          Flere resultater - fortsett å søke...
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted, computed } from 'vue'

export interface Option {
  value: string | number
  label: string
}

interface Props {
  modelValue?: string | number | null
  /** Optional id forwarded to the inner input so an external label can associate via for (S6853). */
  id?: string
  placeholder?: string
  disabled?: boolean
  isClearable?: boolean
  preferModelValueDisplay?: boolean
  /** Liste med tillatte verdier (LOV). Sync-modus: options avledes herfra; emitter ikke requestOptions. */
  allowedValues?: (string | number)[] | null
  /** Alternativer fra parent (async-modus). Brukes når allowedValues ikke er satt. */
  options?: Option[]
  loading?: boolean
  hasMore?: boolean
  /** Tom streng = ingen feil. */
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  id: undefined,
  placeholder: '(Søk...)',
  disabled: false,
  isClearable: true,
  preferModelValueDisplay: false,
  allowedValues: null,
  options: () => [],
  loading: false,
  hasMore: false,
  error: ''
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null]
  select: [option: Option | null]
  requestOptions: [payload: { term: string }]
}>()

const inputRef = ref<HTMLInputElement | null>(null)
const searchTerm = ref<string>('')
const showDropdown = ref<boolean>(false)
const isFocused = ref<boolean>(false)
const dropdownPanelStyle = ref<Record<string, string>>({})

let blurTimer: ReturnType<typeof setTimeout> | null = null
const scrollCleanups: Array<() => void> = []

const isSyncMode = computed(() => props.allowedValues != null && Array.isArray(props.allowedValues))

// Teleport-target: nærmeste <dialog> hvis vi er inne i en modal, ellers body.
// En <dialog> vist med showModal() ligger i nettleserens top layer; et panel
// teleportert til body havner bak modalen og blir usynlig. Barn av dialogen er i
// samme top layer. (Samme konvensjon som BaseDropdownMenu / BaseTooltip.)
const teleportTarget = computed((): string | HTMLElement => {
  if (typeof document === 'undefined') return 'body'
  const dialog = inputRef.value?.closest('dialog')
  return dialog ?? 'body'
})

const displayOptions = computed((): Option[] => {
  if (isSyncMode.value) {
    const term = searchTerm.value.toLowerCase()
    return props.allowedValues!
      .filter((v: string | number) => !term || String(v).toLowerCase().includes(term))
      .slice(0, 20)
      .map((v: string | number) => ({ value: v, label: String(v) }))
  }
  return props.options ?? []
})

// Uten aktivt søk pinnes valgt verdi øverst (markert, deduplisert) så brukeren ser
// hva som er valgt selv om verdien ikke er i første side fra serveren.
const renderOptions = computed((): Option[] => {
  const base = displayOptions.value
  if (props.modelValue === null || props.modelValue === undefined || searchTerm.value !== '') {
    return base
  }
  const selected = base.find((opt: Option) => opt.value === props.modelValue)
    ?? { value: props.modelValue, label: String(props.modelValue) }
  return [selected, ...base.filter((opt: Option) => opt.value !== props.modelValue)]
})

const displayLoading = computed(() => !isSyncMode.value && props.loading)

const displayHasMore = computed(() => {
  if (isSyncMode.value) {
    const term = searchTerm.value.toLowerCase()
    const filtered = props.allowedValues!.filter(
      (v: string | number) => !term || String(v).toLowerCase().includes(term)
    )
    return filtered.length > 20
  }
  return props.hasMore
})

const displayError = computed(() => String(props.error ?? '').trim())

const hasError = computed(() => displayError.value.length > 0)

function collectScrollParents (el: HTMLElement | null): HTMLElement[] {
  const list: HTMLElement[] = []
  if (!el) return list
  let p: HTMLElement | null = el.parentElement
  while (p && p !== document.body) {
    const st = getComputedStyle(p)
    if (/(auto|scroll|overlay)/.test(st.overflowY) || /(auto|scroll|overlay)/.test(st.overflowX)) {
      list.push(p)
    }
    p = p.parentElement
  }
  return list
}

function detachScrollListeners (): void {
  while (scrollCleanups.length > 0) {
    const fn = scrollCleanups.pop()
    fn?.()
  }
}

function updateDropdownPosition (): void {
  const el = inputRef.value
  if (!el || !showDropdown.value) return
  const rect = el.getBoundingClientRect()
  const gap = 2
  const maxH = 240
  const below = window.innerHeight - rect.bottom - gap - 8
  const above = rect.top - gap - 8
  const openUp = below < 100 && above > below
  const maxHeight = Math.min(maxH, Math.max(80, openUp ? above : below))
  if (openUp) {
    dropdownPanelStyle.value = {
      position: 'fixed',
      top: 'auto',
      bottom: `${window.innerHeight - rect.top + gap}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      maxHeight: `${maxHeight}px`,
      zIndex: '10050'
    }
  } else {
    dropdownPanelStyle.value = {
      position: 'fixed',
      top: `${rect.bottom + gap}px`,
      bottom: 'auto',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      maxHeight: `${maxHeight}px`,
      zIndex: '10050'
    }
  }
}

function attachPositionListeners (): void {
  if (!showDropdown.value) {
    return
  }
  detachScrollListeners()
  const onMove = () => {
    updateDropdownPosition()
  }
  window.addEventListener('resize', onMove)
  scrollCleanups.push(() => window.removeEventListener('resize', onMove))
  for (const parent of collectScrollParents(inputRef.value)) {
    parent.addEventListener('scroll', onMove, true)
    scrollCleanups.push(() => parent.removeEventListener('scroll', onMove, true))
  }
}

const displayValue = computed(() => {
  if (isFocused.value && searchTerm.value) {
    return searchTerm.value
  }
  if (props.modelValue !== null && props.modelValue !== undefined) {
    if (props.preferModelValueDisplay) {
      return String(props.modelValue)
    }
    const option = displayOptions.value.find((opt: Option) => opt.value === props.modelValue)
    if (option) {
      return option.label
    }
    if (isSyncMode.value) {
      const valueInAllowed = props.allowedValues!.find((val: string | number) => val === props.modelValue)
      if (valueInAllowed !== undefined) {
        return String(valueInAllowed)
      }
    }
    return String(props.modelValue)
  }
  return ''
})

function emitRequestOptions (term: string): void {
  if (!isSyncMode.value) {
    emit('requestOptions', { term })
  }
}

function onSearchInput (event: Event): void {
  const target = event.target as HTMLInputElement
  searchTerm.value = target.value
  emitRequestOptions(searchTerm.value)
}

function onFocus (): void {
  if (blurTimer !== null) {
    clearTimeout(blurTimer)
    blurTimer = null
  }
  isFocused.value = true
  showDropdown.value = true
  // Marker eksisterende tekst så første tastetrykk erstatter den og starter nytt søk;
  // options hentes med tom term så lista viser første side, ikke kun valgt verdi.
  if (props.modelValue !== null && props.modelValue !== undefined) {
    inputRef.value?.select()
  }
  if (!isSyncMode.value) {
    emitRequestOptions('')
  }
  updateDropdownPosition()
  attachPositionListeners()
}

function onBlur (): void {
  if (blurTimer !== null) {
    clearTimeout(blurTimer)
  }
  blurTimer = setTimeout(() => {
    blurTimer = null
    isFocused.value = false
    showDropdown.value = false
    searchTerm.value = ''
    detachScrollListeners()
  }, 150)
}

function closeDropdown (): void {
  showDropdown.value = false
  searchTerm.value = ''
  detachScrollListeners()
  inputRef.value?.blur()
}

function onChevronClick (): void {
  if (showDropdown.value) {
    closeDropdown()
    return
  }
  inputRef.value?.focus()
  onFocus()
}

function selectFirstResult (): void {
  if (renderOptions.value.length > 0) {
    selectOption(renderOptions.value[0])
  }
}

function selectOption (opt: Option): void {
  // Re-valg av allerede valgt verdi er ingen endring — lukk uten å emitte (unngår unødig lagring).
  if (props.modelValue !== null && props.modelValue !== undefined && opt.value === props.modelValue) {
    closeDropdown()
    return
  }
  emit('update:modelValue', opt.value)
  emit('select', opt)
  closeDropdown()
}

function clearValue (): void {
  emit('update:modelValue', null)
  emit('select', null)
  closeDropdown()
}

onUnmounted(() => {
  if (blurTimer !== null) {
    clearTimeout(blurTimer)
    blurTimer = null
  }
  detachScrollListeners()
})
</script>

